/**
 * Cache utility for API responses
 * Caches data until the end of the current day
 */
class CacheService {
  constructor () {
    this.cache = new Map()
  }

  /**
   * Get cache key for end-of-day expiration
   * @returns {string} Cache key that expires at end of day
   */
  getEndOfDayKey () {
    const now = new Date()
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    return `end_of_day_${endOfDay.getTime()}`
  }

  /**
   * Check if cache has expired (past end of day)
   * @param {string} cacheKey - The cache key to check
   * @returns {boolean} True if cache has expired
   */
  isExpired (cacheKey) {
    if (!cacheKey.startsWith('end_of_day_')) {
      return true
    }

    const timestamp = parseInt(cacheKey.replace('end_of_day_', ''))
    const now = new Date()
    return now.getTime() > timestamp
  }

  /**
   * Get cached data if not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if expired/not found
   */
  get (key) {
    const cacheKey = this.getEndOfDayKey()
    const fullKey = `${key}_${cacheKey}`

    if (this.cache.has(fullKey)) {
      console.log(`Cache hit for key: ${key}`)
      return this.cache.get(fullKey)
    }

    console.log(`Cache miss for key: ${key}`)

    // Clean up expired entries
    this.cleanup()

    return null
  }

  /**
   * Set cached data with end-of-day expiration
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   */
  set (key, data) {
    const cacheKey = this.getEndOfDayKey()
    const fullKey = `${key}_${cacheKey}`

    this.cache.set(fullKey, data)
    console.log(`Cached data for key: ${key} until end of day`)

    // Clean up expired entries
    this.cleanup()
  }

  /**
   * Check if data is cached and not expired
   * @param {string} key - Cache key
   * @returns {boolean} True if valid cached data exists
   */
  has (key) {
    const cacheKey = this.getEndOfDayKey()
    const fullKey = `${key}_${cacheKey}`

    return this.cache.has(fullKey)
  }

  /**
   * Clear expired cache entries
   */
  cleanup () {
    const keysToDelete = []

    for (const [key] of this.cache) {
      if (this.isExpired(key)) {
        keysToDelete.push(key)
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key))
  }

  /**
   * Clear all cache entries
   */
  clear () {
    this.cache.clear()
    console.log('Cache cleared')
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats () {
    const now = new Date()
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    const timeUntilExpiry = endOfDay.getTime() - now.getTime()

    return {
      totalEntries: this.cache.size,
      expiresAt: endOfDay.toISOString(),
      timeUntilExpiry: Math.ceil(timeUntilExpiry / 1000 / 60), // minutes
      cacheKeys: Array.from(this.cache.keys())
    }
  }
}

export default new CacheService()
